import { nextTick, reactive, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import { getVideojsControlBar } from 'src/lib/videojsHelper';
import type {
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

const DEFAULT_PLAY_SPEED = 1.0;

interface Deps {
  enqueueUiTask: (task: () => Promise<void> | void) => void;
  getVjsForLr: (lr: LeftRight) => VideoJsPlayer | undefined;
  playMovieBackward: (lr: LeftRight) => void;
  pauseMovieBackward: (lr: LeftRight) => void;
  resetReversePlayIntervals: () => void;
}

interface MoviePlayerCompareModeControlContext {
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  playMovie: (lr_: BothLeftRight) => void;
  pauseMovie: (lr_: BothLeftRight) => void;
  playUI: (lr_: BothLeftRight) => void;
  pauseUI: (lr_: BothLeftRight) => void;
  tickMovieBackward: (lr_: BothLeftRight) => Promise<void>;
  tickMovieForward: (lr_: BothLeftRight) => Promise<void>;
  setPlaySpeed: (lr_: BothLeftRight, val: number) => Promise<void>;
  setVolume: (lr_: BothLeftRight, val: number) => Promise<void>;
  playModeChange: (lr_: BothLeftRight, isPlayBackward: boolean) => void;
  resetToDefault: (lr: LeftRight) => Promise<void>;
}

// useMoviePlayerCompareModePlayback.tsから分割した共通再生制御
// （play/pause/コマ送り/速度/音量/逆再生モード切替）。
// 逆再生の実駆動はuseMoviePlayerCompareModeReversePlay.tsに委譲し、
// vjsインスタンス自体はdeps.getVjsForLr経由でのみ扱う。
export function useMoviePlayerCompareModeControl(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeControlContext> {
  const getUIPlayState = (lr: LeftRight) => {
    return state[lr].playback.isPlaying ? 'playing' : 'paused';
  };
  const playMovie = (lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      state[lr].playback.isPlaying = true;
      if (!state[lr].playback.isPlayBackward) {
        // 逆再生をリセットする
        // 左右個別の再生があるので、逆再生する場合は個別にリセットする
        deps.resetReversePlayIntervals();
        // control-areaのemit経由でのみ呼び出される想定.
        // 再生させたい場合はplayUI()を呼ぶ.
        // playのpromiseが解決しない内に再度呼び出すとエラーを吐くようなのでケアする.
        deps.enqueueUiTask(async() => {
          const vjs = deps.getVjsForLr(lr);
          try {
            await (vjs?.play() || Promise.resolve());
          } catch {
            // 再生promiseが解決しない内に再度呼び出すとエラーを吐くようなのでケアする
          }
        });
      } else {
        deps.playMovieBackward(lr);
      }
    });
  };
  const pauseMovie = (lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    // control-areaのemit経由でのみ呼び出される想定.
    // 再生させたい場合はpauseUI()を呼ぶ.
    lrs.forEach(lr => {
      state[lr].playback.isPlaying = false;
      void (async() => {
        try {
          await state.uiPromise;
          const vjs = deps.getVjsForLr(lr);
          if (vjs) {
            vjs.pause();
          }
        } catch (e) {
          console.error(e);
        }
      })();
      const vjs = deps.getVjsForLr(lr);
      if (vjs && vjs.hasClass('vjs-playing')) {
        deps.pauseMovieBackward(lr);
      }
    });
  };
  const playUI = (lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      if (lr_ === 'both') {
        state[lr].playback.isPlayBackward = state.both.isPlayBackward;
      }
      playMovie(lr);
    });
    // 同時操作部分の表示
    if (lr_ === 'both') {
      state.both.isPlaying = true;
    } else {
      nextTick(() => {
        if (state.left.playback.isPlaying && state.right.playback.isPlaying) {
          state.both.isPlaying = true;
        }
      });
    }
  };
  const pauseUI = (lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      pauseMovie(lr);
    });
    // 同時操作部分の表示
    if (lr_ === 'both') {
      state.both.isPlaying = false;
    } else {
      nextTick(() => {
        if (!state.left.playback.isPlaying && !state.right.playback.isPlaying) {
          state.both.isPlaying = false;
        }
      });
    }
  };
  const tickMovieBackward = async(lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      pauseUI(lr);
      // 同一の動画内でのみコマ戻し
      const vjs = deps.getVjsForLr(lr);
      if (!vjs) return;

      let currentMsec = parseInt((vjs.currentTime() * 1000).toString());
      currentMsec -= 100; // 0.1秒
      vjs.currentTime(currentMsec / 1000);
    });
  };
  const tickMovieForward = async(lr_: BothLeftRight) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      pauseUI(lr);
      // 同一の動画内でのみコマ送り
      const vjs = deps.getVjsForLr(lr);
      if (!vjs) return;

      let currentMsec = parseInt((vjs.currentTime() * 1000).toString());
      currentMsec += 100; // 0.1秒
      vjs.currentTime(currentMsec / 1000);
    });
  };
  const setPlaySpeed = async(lr_: BothLeftRight, val: number) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      state[lr].playback.currentPlaySpeed = val;
      const vjs = deps.getVjsForLr(lr);
      if (vjs) {
        vjs.playbackRate(val);
      }
    });
  };
  const setVolume = async(lr_: BothLeftRight, val: number) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      const vjs = deps.getVjsForLr(lr);
      if (vjs) {
        vjs.volume(val);
      }
    });
  };
  const playModeChange = (lr_: BothLeftRight, isPlayBackward: boolean) => {
    const lrs: Array<LeftRight> = lr_ === 'both' ? ['left', 'right'] : [lr_];
    lrs.forEach(lr => {
      state[lr].playback.isPlayBackward = isPlayBackward;
    });
    if (lr_ === 'both') {
      state.both.isPlayBackward = isPlayBackward;
    }
    if (lr_ !== 'both') return;

    lrs.forEach(lr => {
      const vjs = deps.getVjsForLr(lr);
      if (!vjs) return;

      const controlBar = getVideojsControlBar(vjs).el();
      const playMovieBtn = controlBar.querySelector('.vjs-play-movie');
      const playMovieBackwardCheckbox = controlBar.querySelector('.vjs-play-movie-backward') as HTMLInputElement;
      if (!playMovieBtn || !playMovieBackwardCheckbox) return;

      playMovieBackwardCheckbox.checked = isPlayBackward;
      if (isPlayBackward) {
        playMovieBtn.classList.add('play-backward');
        return;
      }
      playMovieBtn.classList.remove('play-backward');
    });
  };
  // orchestrator(useMoviePlayerCompareMode)が動画切替・再生開始のたびに
  // 呼ぶUIリセット処理をここに集約する。
  // isPlayBackward/currentPlaySpeed/volumeはいずれもplaybackが所有するフィールドなので、
  // 呼び出し元からの直接書き換えではなく本関数経由にする。
  const resetToDefault = async(lr: LeftRight) => {
    pauseUI(lr);
    state[lr].playback.isPlayBackward = false;
    await setPlaySpeed(lr, DEFAULT_PLAY_SPEED);
    await setVolume(lr, 0);
  };

  return reactive({
    getUIPlayState,
    playMovie,
    pauseMovie,
    playUI,
    pauseUI,
    tickMovieBackward,
    tickMovieForward,
    setPlaySpeed,
    setVolume,
    playModeChange,
    resetToDefault,
  });
}
